/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactNode } from 'react';

import { type GetBotInfo } from '@adp-common/chat-uikit-shared';
import {
  type FileMixItem,
  type TextMixItem,
  type ImageMixItem,
} from '@adp-common/chat-core';

import { type IMessageContentProps } from '../text-content';
import { type IImageMessageContentProps } from '../image-content';
import { type IProps as IFileContentProps } from '../file-content';
import {
  isFileMixItem,
  isImageMixItem,
  isMultimodalContentListLike,
  isTextMixItem,
} from '../../../utils/multimodal';
import { TextItemList } from './text-item-list';
import { ImageItemList } from './image-item-list';
import { FileItemList } from './file-item-list';

import './index.less';

export type MultimodalContentProps = IImageMessageContentProps &
  IFileContentProps &
  Pick<
    IMessageContentProps,
    'mdBoxProps' | 'onLinkClick' | 'enableAutoSizeImage'
  > & {
    getBotInfo: GetBotInfo;
    renderTextContentAddonTop?: ReactNode;
    isContentLoading: boolean | undefined;
  };

/**
 * This component is not simple and should not actually be called Content.
 */

// TODO: @liushuoyan provides the switch~~
export const MultimodalContent: React.FC<MultimodalContentProps> = ({
  renderTextContentAddonTop,
  message,
  getBotInfo,
  fileAttributeKeys,
  copywriting: fileCopywriting,
  onCancel,
  onCopy,
  onRetry,
  readonly,
  onImageClick,
  layout,
  showBackground,
  isContentLoading,
  mdBoxProps,
  onLinkClick,
  enableAutoSizeImage,
}) => {
  const { content_obj } = message;
  if (!isMultimodalContentListLike(content_obj)) {
    // TODO: Broke news should need to add a unified bottom line and report
    return null;
  }

  const fileItemList = content_obj.item_list.filter(
    (item): item is FileMixItem => isFileMixItem(item),
  );

  const textItemList = content_obj.item_list.filter(
    (item): item is TextMixItem => isTextMixItem(item),
  );

  const imageItemList = content_obj.item_list.filter(
    (item): item is ImageMixItem => isImageMixItem(item),
  );

  const text = (
    <TextItemList
      readonly={readonly}
      mdBoxProps={mdBoxProps}
      onLinkClick={onLinkClick}
      onImageClick={onImageClick}
      enableAutoSizeImage={enableAutoSizeImage}
      textItemList={textItemList}
      renderTextContentAddonTop={renderTextContentAddonTop}
      message={message}
      showBackground={showBackground}
      getBotInfo={getBotInfo}
      isContentLoading={isContentLoading}
    />
  );
  const images = (
    <ImageItemList
      imageItemList={imageItemList}
      message={message}
      onImageClick={onImageClick}
    />
  );
  return (
    <>
      {message.role === 'assistant' ? text : null}
      {message.role === 'assistant' ? images : null}
      <FileItemList
        fileItemList={fileItemList}
        fileAttributeKeys={fileAttributeKeys}
        fileCopywriting={fileCopywriting}
        readonly={readonly}
        onRetry={onRetry}
        onCancel={onCancel}
        onCopy={onCopy}
        message={message}
        layout={layout}
        showBackground={showBackground}
      />

      {message.role === 'assistant' ? null : images}
      {message.role === 'assistant' ? null : text}
    </>
  );
};

MultimodalContent.displayName = 'MultimodalContent';
